import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Accordion, AccordionContent, AccordionTitle, Button, Header, Icon, List, Loader, Message, Segment} from "semantic-ui-react";
import store, {isInProgress} from "./PrintLabelsWidgetStore";
import {LabelPrintTaskDto, LabelPrintTaskStatus, PopupDataDto} from "./Dtos";
import errorConverter from "../../Services/ErrorConverter";

const maxShownOrders = 20;

function PrintLabelsWidgetPage(props: { contextKey: string }) {
    const {contextKey} = props;
    useEffect(() => {
        function handleEvent(e: any) {
            const data = e.data as PopupDataDto;
            if (!data || !data.popupParameters) return;

            if (data.name === "OpenPopup") {
                void store.init(contextKey, data.popupParameters.orderHrefIds);
            }
        }

        window.addEventListener('message', handleEvent);
        return function cleanup() {
            window.removeEventListener('message', handleEvent);
            store.cleanup();
        }
    }, [contextKey]);

    if (store.isLoading) {
        return <Segment basic><Loader active inline='centered'>Загрузка</Loader></Segment>
    }

    const selectedCount = store.orderHrefIds.length;
    return <Segment basic>
        <Header>
            {selectedCount > 0 ? `Выбрано ${selectedCount} ${getOrdersCaption(selectedCount)}` : "Заказы не выбраны"}
        </Header>

        {selectedCount > 0
            ? <Button primary disabled={!store.canPrint} loading={store.isPrinting} onClick={() => store.print()}>
                <Icon name='print'/> {store.printedTasks.length > 0 ? "Напечатать ещё раз" : "Печать"}
            </Button>
            : <Message info content='Выберите заказы в списке, чтобы напечатать этикетки'/>}

        <PrintResult/>
        <History/>
    </Segment>
}

const PrintResult = observer(() => {
    if (store.printedTasks.length === 0) {
        return null;
    }

    return <Segment basic>
        <SkippedOrders/>
        {store.printedTasks.map(task => <LabelTaskAccordion key={task.id} task={task} defaultActive={true}/>)}
    </Segment>
});

/**
 * Заказы, для которых этикетки не печатаются. Идентификаторы МоегоСклада пользователю
 * ни о чём не говорят, поэтому показываем причины и сколько заказов на каждую
 */
const SkippedOrders = observer(() => {
    if (store.skipped.length === 0) {
        return null;
    }

    const countByReason: { [reason: string]: number } = {};
    store.skipped.forEach(x => countByReason[x.reason] = (countByReason[x.reason] || 0) + 1);

    return <Message warning>
        <Message.Header>
            Напечатано {store.printedOrdersCount} из {store.printedOrdersCount + store.skipped.length}
        </Message.Header>
        <List>
            {Object.keys(countByReason).map(reason => <List.Item key={reason}>
                {countByReason[reason]} {getOrdersCaption(countByReason[reason])} пропущено:{' '}
                {errorConverter.getCaptionFormCode(reason)}
            </List.Item>)}
        </List>
    </Message>
});

const History = observer(() => {
    if (store.history.length === 0) {
        return null;
    }

    return <Segment basic>
        <Header size='small'>История</Header>
        {store.history.map(task => <LabelTaskAccordion key={task.id} task={task} defaultActive={false}/>)}
    </Segment>
});

/**
 * Задача печати в виде сворачиваемой панели: в заголовке — аккаунт, количество, дата и статус,
 * в контенте — список заказов и блок статуса (спиннер, скачивание или ошибка)
 */
const LabelTaskAccordion = observer((props: { task: LabelPrintTaskDto, defaultActive: boolean }) => {
    const {task, defaultActive} = props;
    const [isActive, setIsActive] = useState(defaultActive);

    return <Accordion fluid styled>
        <AccordionTitle active={isActive} onClick={() => setIsActive(!isActive)}>
            <Icon name='dropdown'/>
            {getTaskCaption(task)}
            <TaskStatusIcon task={task}/>
        </AccordionTitle>
        <AccordionContent active={isActive}>
            <TaskOrders task={task}/>
            <TaskStatusBlock task={task}/>
        </AccordionContent>
    </Accordion>
});

/**
 * Идентификаторы заказов Авито (MarketplaceId), для которых напечатаны этикетки
 */
const TaskOrders = observer((props: { task: LabelPrintTaskDto }) => {
    const {task} = props;
    if (task.orders.length === 0) {
        return null;
    }

    const shown = task.orders.slice(0, maxShownOrders);
    const restCount = task.orders.length - shown.length;
    return <p>
        Заказы: {shown.join(', ')}
        {restCount > 0 && ` и ещё ${restCount}`}
    </p>
});

/**
 * Текущее состояние задачи: формирование, готовый файл или ошибка
 */
const TaskStatusBlock = observer((props: { task: LabelPrintTaskDto }) => {
    const {task} = props;
    if (isInProgress(task)) {
        return <Message icon>
            <Icon name='circle notched' loading/>
            <Message.Content>
                <Message.Header>Этикетки формируются</Message.Header>
                Обычно это занимает меньше минуты
            </Message.Content>
        </Message>
    }

    if (task.status === LabelPrintTaskStatus.Ready) {
        return <div style={{marginTop: '10px'}}>
            <DownloadButton task={task}/>
        </div>
    }

    return <Message negative>
        <Message.Header>Не удалось сформировать этикетки</Message.Header>
        {task.error ? errorConverter.getCaptionFormCode(task.error) : "Попробуйте напечатать ещё раз"}
    </Message>
});

function TaskStatusIcon(props: { task: LabelPrintTaskDto }) {
    const {task} = props;
    if (isInProgress(task)) {
        return <Icon name='circle notched' loading/>
    }

    if (task.status === LabelPrintTaskStatus.Ready) {
        return <Icon name='check circle' color='green'/>
    }

    return <Icon name='warning sign' color='red'/>
}

function DownloadButton(props: { task: LabelPrintTaskDto }) {
    const {task} = props;
    // ручка отдаёт файл вложением, поэтому вкладка не открывается и страница попапа остаётся на месте
    return <Button as='a' href={store.getDownloadUrl(task.id)} download size='small'>
        <Icon name='download'/> Скачать PDF
    </Button>
}

function getTaskCaption(task: LabelPrintTaskDto) {
    return `Аккаунт «${task.avitoAccountName}», ${task.ordersCount} ${getOrdersCaption(task.ordersCount)}, ${formatDate(task.createdOn)}`;
}

function getOrdersCaption(count: number) {
    const lastTwoDigits = count % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return "заказов";
    }

    switch (count % 10) {
        case 1:
            return "заказ";
        case 2:
        case 3:
        case 4:
            return "заказа";
        default:
            return "заказов";
    }
}

function formatDate(date: string) {
    return new Date(date).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
}

export default observer(PrintLabelsWidgetPage);
